/**
 * Cable profiles -- group 02.
 *
 * Straight Wire, Tara Labs, Siltech, Crystal Cable, van den Hul,
 * The Chord Company, QED and Atlas Cables.
 *
 * None of these eight brands publishes a full resistance / inductance /
 * capacitance set that this author can quote from memory with confidence, so
 * every entry below lets the kit derive the three electrical figures from
 * gauge, conductor material and geometry. QED and van den Hul do print loop
 * resistance, inductance and capacitance on their own data sheets; the correct
 * move is to copy those sheets in later rather than to approximate them here,
 * and the provenance notes say so.
 *
 * Power, USB and ethernet entries describe construction only. The engine
 * applies no signal processing to those three types, and nothing in their notes
 * implies otherwise.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_02: CableProfile[] = [
  // =========================================================================
  // Straight Wire (United States)
  // =========================================================================
  makeCable({
    mfr: 'Straight Wire',
    model: 'Musicable II',
    cableType: 'speaker',
    price: usd(160, 'APPROXIMATE', 2024, 'Approximate street price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['entry-level', 'derived-electrical'],
    notes:
      'Entry two-conductor stranded OFC in a flat side-by-side jacket. At 14 AWG the loop resistance over a 3 m run is a few hundredths of an ohm, which against a nominal 8 ohm load is a loss well under a tenth of a decibel, and against a 3 ohm impedance minimum becomes just visible in the frequency response the engine computes.',
    provenanceNote:
      'Conductor material and jacket format from the manufacturer description. Straight Wire does not publish AWG for this model, so the gauge is inferred from the stated conductor count and cross-section, and resistance, inductance and capacitance are derived by the kit rather than quoted.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Octave II',
    cableType: 'speaker',
    price: usd(380, 'APPROXIMATE', 2024, 'Approximate street price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['mid-line', 'derived-electrical'],
    notes:
      'Multiple stranded OFC groups counter-wound so that adjacent runs carry opposing current. That arrangement lowers loop inductance relative to a simple parallel pair and raises capacitance in exchange, which is the standard trade and is harmless driving a loudspeaker. The heavier 12 AWG conductor is the part that actually changes the divider between amplifier and load.',
    provenanceNote:
      'Geometry and conductor material from the manufacturer description; gauge inferred from the stated cross-section. No electrical figures are published for this model, so all three are derived.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Quartet II',
    cableType: 'speaker',
    price: usd(650, 'APPROXIMATE', 2024, 'Approximate street price for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['mid-line', 'bi-wire-available', 'derived-electrical'],
    notes:
      'Four conductor groups woven into a single run, offered in a bi-wire arrangement that splits the groups at the loudspeaker end. Bi-wiring does not change the amplifier output impedance, but it does put separate loop resistance in each of the two paths, and the engine computes that from the gauge given here.',
    provenanceNote:
      'Conductor count and geometry from the manufacturer description; gauge inferred from total copper cross-section. Electrical figures derived, not published.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Encore II',
    cableType: 'speaker',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 11,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['upper-line', 'derived-electrical'],
    notes:
      'Many individually insulated strands bundled in graded groups. Individually insulated strands change the current distribution across the bundle at high frequency relative to a solid conductor of the same area; the DC loop resistance, which is what dominates a loudspeaker run, is set by the total copper area and is unchanged by the insulation.',
    provenanceNote:
      'Litz-style stranding and dielectric from the manufacturer description; gauge inferred from total conductor area. Straight Wire publishes no R, L or C figures, so those are derived.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Crescendo',
    cableType: 'speaker',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 10,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['flagship', 'heavy-gauge', 'derived-electrical'],
    notes:
      'The heaviest cable in the range, a large insulated-strand bundle with a total copper area equivalent to about 10 AWG. Doubling copper area halves loop resistance; over a 2.5 m run that difference is a small fraction of an ohm, which matters most into loudspeakers whose impedance dips low rather than into an easy 8 ohm load.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from total conductor area. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Chorus II',
    cableType: 'interconnect',
    price: usd(80, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided copper shield',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['entry-level', 'single-ended', 'derived-electrical'],
    notes:
      'A conventional shielded coaxial single-ended interconnect. At coaxial capacitance a 1 m run loads the source with a low hundreds of picofarads, which against a typical solid-state output impedance of a few hundred ohms puts the low-pass corner in the megahertz. The engine computes the in-band result and it rounds to nothing.',
    provenanceNote:
      'Shield and geometry from the manufacturer description; gauge inferred from the class. No published R, L or C for this model, so all three are derived.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Serenade II',
    cableType: 'interconnect',
    price: usd(420, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid, drained at one end on the single-ended version',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['mid-line', 'balanced-available', 'derived-electrical'],
    notes:
      'Twisted signal pair inside a foil and braid shield, terminated either single-ended or balanced. A twisted pair spaces the conductors further apart than a coax does, which lowers capacitance and raises inductance; on a 1 m line-level run neither figure produces a change the engine can report above 0.00 dB in band.',
    provenanceNote:
      'Shield construction and termination options from the manufacturer description; gauge inferred from the class. Electrical figures derived rather than published.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Symphony II',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['upper-line', 'silver-plated', 'balanced-available', 'derived-electrical'],
    notes:
      'Silver-plated copper conductors in a shielded twisted pair. Silver plating is a surface treatment: the DC path is still the copper core, so the kit derives resistance from copper resistivity rather than silver, and the plating changes nothing the engine models at audio frequencies.',
    provenanceNote:
      'Plating and shield from the manufacturer description; gauge inferred from the class. Resistance is derived from the copper core, which is the honest treatment of a plated conductor at audio frequencies.',
  }),
  makeCable({
    mfr: 'Straight Wire',
    model: 'Rhapsody S',
    cableType: 'interconnect',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Multi-layer foil and braid',
    dielectric: 'Foamed polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d08770',
    tags: ['flagship', 'silver-plated', 'derived-electrical'],
    notes:
      'Top of the analogue interconnect range: a heavier plated conductor in a foamed dielectric under multiple shield layers. A foamed dielectric has a lower permittivity than solid polyethylene and therefore gives lower capacitance per metre for the same spacing, which is the one electrical consequence of the construction that the engine can actually act on.',
    provenanceNote:
      'Dielectric and shielding from the manufacturer description; gauge inferred from the class. Straight Wire publishes no R, L or C, so those are derived.',
  }),

  // =========================================================================
  // Tara Labs (United States)
  // =========================================================================
  makeCable({
    mfr: 'Tara Labs',
    model: 'RSC Prime 500',
    cableType: 'interconnect',
    price: usd(230, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Foil shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['entry-level', 'rectangular-solid-core', 'derived-electrical'],
    notes:
      'Entry cable using the house rectangular solid-core conductor. A solid conductor with wide spacing gives the low capacitance and comparatively high inductance typical of the geometry; on a metre of line-level cable the inductance is irrelevant against a load in the tens of kilohms and the low capacitance keeps the computed corner frequency far above the audio band.',
    provenanceNote:
      'Conductor form and shielding from the manufacturer description; gauge inferred from the stated conductor dimensions. Tara Labs does not publish R, L and C for this model, so all three are derived.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'Prism Klara',
    cableType: 'interconnect',
    price: usd(130, 'DISCONTINUED_USED', 2024, 'Typical used price for a 1 m pair; the Prism line is long discontinued.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    shielding: 'Spiral shield',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['discontinued', 'single-ended', 'derived-electrical'],
    notes:
      'An older, simple shielded twisted pair from the Prism range. Included because it is the electrical baseline the rest of the catalogue departs from: an ordinary twisted pair of this gauge is what everything more elaborate is compared against.',
    provenanceNote:
      'Discontinued model. Construction from period manufacturer literature; gauge inferred from the class and price is a used-market observation rather than a retail figure. Electrical figures derived.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'Vector II',
    cableType: 'interconnect',
    price: usd(180, 'DISCONTINUED_USED', 2024, 'Typical used price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 22,
    shielding: 'Foil plus drain wire',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['discontinued', 'rectangular-solid-core', 'derived-electrical'],
    notes:
      'Earlier solid-core design with a foil shield and drain. Same electrical family as the current entry cables: low capacitance per metre, higher inductance, neither of which is consequential over a metre into a line input.',
    provenanceNote:
      'Discontinued model, construction from period literature. Gauge inferred; price is a used-market observation. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'The Two',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'solid-core',
    gauge: 21,
    shielding: 'Floating shield with a separate ground path',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['mid-line', 'rectangular-solid-core', 'derived-electrical'],
    notes:
      'Rectangular solid-core conductors of a higher-purity continuous-cast copper, with the shield returned on its own path rather than through the signal ground. Separating the shield return keeps shield current out of the signal return; the difference is in the noise domain, not in the resistance, inductance and capacitance the engine reads.',
    provenanceNote:
      'Conductor form and shield arrangement from the manufacturer description; gauge inferred from stated conductor dimensions. No published electrical figures, so all three are derived.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'Zero Gold',
    cableType: 'interconnect',
    price: usd(6800, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 20,
    dielectric: 'Air, conductors suspended in extruded tubes',
    shielding: 'Separate shield conductor',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['upper-line', 'air-dielectric', 'derived-electrical'],
    notes:
      'Solid-core conductors suspended in air-filled tubes. Air has the lowest practical permittivity of any dielectric, so for a given conductor spacing this construction gives about the lowest capacitance per metre available and correspondingly the highest inductance. On an interconnect that trade is entirely in the right direction, and it is also close to inaudible over a metre.',
    provenanceNote:
      'Air-tube construction from the manufacturer description; gauge inferred from stated conductor dimensions. Tara Labs publishes no numeric R, L or C for this model, so the kit derives them from the air-tube geometry.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'The Zero Evolution',
    cableType: 'interconnect',
    price: usd(14000, 'APPROXIMATE', 2024, 'Approximate street price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 19,
    dielectric: 'Air, conductors suspended in extruded tubes',
    shielding: 'Separate shield conductor with its own ground return',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['flagship', 'air-dielectric', 'derived-electrical'],
    notes:
      'The flagship interconnect: a large bundle of rectangular solid-core conductors in air tubes with the shield carried on its own return. Electrically it is the same family as the rest of the air-tube range, with slightly more copper and therefore slightly lower series resistance -- a quantity that is already negligible at line level.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from stated conductor count and dimensions. Electrical figures derived, since none are published.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'Prime 1000',
    cableType: 'speaker',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['entry-level', 'rectangular-solid-core', 'derived-electrical'],
    notes:
      'Entry speaker cable using several rectangular solid-core conductors per polarity. Widely spaced solid conductors give higher inductance than a braid; over a 2.5 m run that inductance contributes a fraction of an ohm of reactance at 20 kHz, which the engine puts in series with the loudspeaker impedance along with the resistance.',
    provenanceNote:
      'Conductor form from the manufacturer description; gauge inferred from total conductor area. Tara Labs does not publish R, L and C for this model, so those are derived.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'RSC Air 1',
    cableType: 'speaker',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Air-filled polyethylene tubes',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['mid-line', 'air-dielectric', 'derived-electrical'],
    notes:
      'Air-tube construction applied to a loudspeaker run. Note that the trade runs the wrong way here compared with an interconnect: air spacing minimises capacitance, which a loudspeaker does not care about, while raising inductance, which is the one reactive term that does show up in the amplifier-to-load divider at the top of the band.',
    provenanceNote:
      'Air-tube construction from the manufacturer description; gauge inferred from total conductor area. Electrical figures derived from the geometry rather than published.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'The 0.8',
    cableType: 'speaker',
    price: usd(3600, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 11,
    dielectric: 'Air-filled tubes',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['upper-line', 'air-dielectric', 'derived-electrical'],
    notes:
      'More conductors per polarity than the model below it, in the same air-tube arrangement. The additional copper is the electrically meaningful change: total area sets loop resistance, and loop resistance is the term that adds directly to the amplifier output impedance seen by the loudspeaker.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from total conductor area. No published electrical figures, so all three are derived.',
  }),
  makeCable({
    mfr: 'Tara Labs',
    model: 'Omega Evolution SP',
    cableType: 'speaker',
    price: usd(19000, 'APPROXIMATE', 2024, 'Approximate street price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 10,
    dielectric: 'Air-filled tubes',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4f83cc',
    tags: ['flagship', 'air-dielectric', 'heavy-gauge', 'derived-electrical'],
    notes:
      'The flagship loudspeaker cable: a large count of rectangular solid-core conductors in air tubes, with a total copper area around 10 AWG. That gauge is the part the engine acts on, and it is the same gauge available in far cheaper cable -- which the simulation will show plainly, because it computes the divider from the numbers rather than from the price.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from stated conductor count and dimensions. Electrical figures derived by the kit.',
  }),

  // =========================================================================
  // Siltech (Netherlands)
  // =========================================================================
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Anniversary 330i',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'Kapton and PEEK',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['entry-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'Entry point for the mono-crystal silver metallurgy the company is built on. Silver has about eight per cent lower resistivity than copper for the same cross-section, which the kit applies; at 24 AWG over a metre of interconnect the resulting difference in series resistance is a few milliohms and has no audible consequence into a line input.',
    provenanceNote:
      'Conductor metallurgy and dielectric are documented by the manufacturer. Gauge is inferred from the stated conductor cross-section; Siltech does not publish resistance, inductance or capacitance, so those are derived by the kit.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 380i',
    cableType: 'interconnect',
    price: usd(1900, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'Kapton and PEEK',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['entry-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'First step of the current Classic Legend range, with the same shielded silver pair in a lighter conductor count than the models above it. Construction differences within this range are conductor area and dielectric layering, both of which the kit maps onto resistance and capacitance respectively.',
    provenanceNote:
      'Metallurgy and dielectric documented by the manufacturer; gauge inferred from stated cross-section. Electrical figures derived rather than published.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 680i',
    cableType: 'interconnect',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 23,
    dielectric: 'Kapton and PEEK',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['mid-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'Middle of the Classic Legend interconnects: more silver per conductor than the 380i in the same shielded twisted arrangement. The engine sees a slightly lower series resistance and otherwise identical geometry, and will report the in-band difference honestly, which is to say as approximately zero.',
    provenanceNote:
      'Metallurgy and dielectric documented by the manufacturer; gauge inferred from stated cross-section. No published R, L or C, so those are derived.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 880i',
    cableType: 'interconnect',
    price: usd(6900, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Kapton and PEEK, multi-layer',
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['upper-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'Top of the Classic Legend interconnects, with the largest silver cross-section in that range and a heavier multi-layer shield and dielectric stack. Thicker dielectric increases conductor spacing, which lowers capacitance and raises inductance -- the usual trade, applied here by the kit from the geometry.',
    provenanceNote:
      'Metallurgy, dielectric stack and shield documented by the manufacturer; gauge inferred from stated cross-section. Electrical figures derived.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Double Crown',
    cableType: 'interconnect',
    price: usd(17000, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair; Royal Signature series.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Kapton and PEEK, multi-layer',
    shielding: 'Multi-layer shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['flagship', 'silver-gold', 'derived-electrical'],
    notes:
      'Royal Signature interconnect using the silver-gold conductor, where gold is introduced into the crystal boundaries of the silver. Gold is a poorer conductor than silver, so the alloy has slightly higher resistivity than pure silver, and the kit reflects that rather than assuming the more expensive metal must measure better.',
    provenanceNote:
      'Silver-gold metallurgy is documented by the manufacturer and is the defining feature of this series. Gauge inferred from stated cross-section; no published R, L or C, so those are derived.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Anniversary 330L',
    cableType: 'speaker',
    price: usd(2800, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'litz',
    gauge: 14,
    dielectric: 'Kapton and PEEK',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['entry-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'Entry silver loudspeaker cable, built from many individually insulated mono-crystal silver strands. Silver conducts about eight per cent better than copper of the same area, so a 14 AWG silver run has roughly the loop resistance of a 13.8 AWG copper one -- a real difference, and a much smaller one than the price gap between the two suggests.',
    provenanceNote:
      'Metallurgy and stranding documented by the manufacturer; gauge inferred from stated total cross-section. Siltech does not publish loop resistance, inductance or capacitance, so those are derived.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 680L',
    cableType: 'speaker',
    price: usd(7500, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'litz',
    gauge: 13,
    dielectric: 'Kapton and PEEK',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['mid-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'Larger silver cross-section than the entry model in the same insulated-strand construction. Loop resistance falls in proportion to conductor area, and on a 2 m run into a loudspeaker with a 4 ohm minimum the whole cable contribution is still well under a tenth of an ohm.',
    provenanceNote:
      'Metallurgy and stranding documented by the manufacturer; gauge inferred from stated total cross-section. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 880L',
    cableType: 'speaker',
    price: usd(14000, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'litz',
    gauge: 12,
    dielectric: 'Kapton and PEEK, multi-layer',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['upper-line', 'mono-crystal-silver', 'derived-electrical'],
    notes:
      'The heaviest Classic Legend loudspeaker cable, roughly a 12 AWG equivalent in mono-crystal silver. Gauge and material are the two inputs to the loop-resistance calculation and both are favourable here; the geometry is a strand bundle rather than a braid, so inductance sits in the middle of the range rather than at the low end.',
    provenanceNote:
      'Metallurgy, stranding and dielectric documented by the manufacturer; gauge inferred from stated total cross-section. No published electrical figures, so those are derived.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Emperor Crown',
    cableType: 'speaker',
    price: usd(46000, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair; Royal Signature series.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'litz',
    gauge: 11,
    dielectric: 'Kapton and PEEK, multi-layer',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['flagship', 'silver-gold', 'heavy-gauge', 'derived-electrical'],
    notes:
      'Royal Signature loudspeaker cable in the silver-gold conductor, with the largest cross-section in the catalogue. Worth stating plainly: the gold in the alloy raises resistivity slightly relative to pure silver, so on the two numbers the engine reads this cable is marginally worse than the pure silver model beneath it at the same gauge, and the extra area is what puts it ahead.',
    provenanceNote:
      'Silver-gold metallurgy documented by the manufacturer; gauge inferred from stated total cross-section. Electrical figures derived from material and geometry, not published.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Classic Legend 680P',
    cableType: 'power',
    price: usd(3800, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Braided shield',
    termination: ['IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['mains', 'shielded', 'no-signal-path'],
    notes:
      'A shielded mains cord with mono-crystal silver conductors at roughly 12 AWG, rated for 16 A and offered with C15, C19 and Schuko terminations. The engine applies no signal processing to power cords: selecting this changes the chain description and the price and leaves the audio bit-identical.',
    provenanceNote:
      'Metallurgy, shield and connector options documented by the manufacturer; gauge and current rating inferred from the stated conductor cross-section and the European mains standard the cord is built to.',
  }),
  makeCable({
    mfr: 'Siltech',
    model: 'Ruby Double Crown',
    cableType: 'power',
    price: usd(13000, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord; Royal Signature series.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Multi-layer shield',
    termination: ['IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0c0c0',
    tags: ['mains', 'flagship', 'shielded', 'no-signal-path'],
    notes:
      'The flagship mains cord: silver-gold conductors of about 11 AWG in a multi-layer shield, 16 A rated, with the heavy connector hardware and strain relief that go with a cable of this mass. It is a serious piece of manufacturing and the engine applies exactly zero digital signal processing to it, as it does to every power cord in this database.',
    provenanceNote:
      'Metallurgy, shield construction and connector options documented by the manufacturer; gauge and current rating inferred from stated conductor cross-section and the mains standard.',
  }),

  // =========================================================================
  // Crystal Cable (Netherlands)
  // =========================================================================
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Piccolo Diamond',
    cableType: 'interconnect',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 26,
    dielectric: 'Kapton',
    shielding: 'Concentric silver return acting as the shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['entry-line', 'silver-gold', 'thin-gauge', 'derived-electrical'],
    notes:
      'The thinnest cable in the range and the clearest illustration of the house design: a single fine silver-gold core in a Kapton dielectric with a concentric silver return. Coaxial spacing this close means comparatively high capacitance per metre, which is why the design suits line-level runs and not loudspeaker runs.',
    provenanceNote:
      'Coaxial construction, Kapton dielectric and silver-gold metallurgy are documented by the manufacturer. Gauge is inferred from the stated conductor diameter; no R, L or C figures are published, so those are derived.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Micro Diamond',
    cableType: 'interconnect',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 25,
    dielectric: 'Kapton',
    shielding: 'Concentric silver return',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['mid-line', 'silver-gold', 'derived-electrical'],
    notes:
      'Same coaxial architecture as the entry cable with a larger conductor. Series resistance falls with area, and at line level that resistance is already three orders of magnitude below the input impedance it works into, so the engine will compute a change and report it as nil.',
    provenanceNote:
      'Construction and metallurgy documented by the manufacturer; gauge inferred from stated conductor diameter. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Special Diamond',
    cableType: 'interconnect',
    price: usd(3900, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 24,
    dielectric: 'Kapton',
    shielding: 'Double concentric silver return',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['upper-line', 'silver-gold', 'derived-electrical'],
    notes:
      'A doubled version of the same coaxial element, which is how this range scales: identical geometry per element, more elements in parallel. Paralleling halves resistance and doubles capacitance, and the kit models the geometry as coaxial in either case.',
    provenanceNote:
      'Construction and metallurgy documented by the manufacturer; gauge inferred from combined conductor cross-section. No published electrical figures, so those are derived.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Reference Diamond',
    cableType: 'speaker',
    price: usd(7800, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 15,
    dielectric: 'Kapton',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['mid-line', 'silver-gold', 'derived-electrical'],
    notes:
      'A loudspeaker cable built by paralleling the same fine coaxial elements rather than by using one thick conductor. The total cross-section works out around 15 AWG, which is light for a loudspeaker cable: on a 2 m run into a 4 ohm load the loop resistance is a measurable fraction of an ohm and the engine will show it in the response.',
    provenanceNote:
      'Construction and metallurgy documented by the manufacturer; gauge inferred from the combined cross-section of the parallel elements. Electrical figures derived, not published.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Ultra Diamond',
    cableType: 'speaker',
    price: usd(13000, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 14,
    dielectric: 'Kapton',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['upper-line', 'silver-gold', 'derived-electrical'],
    notes:
      'More parallel coaxial elements than the model below, giving a total cross-section near 14 AWG. The concentric return means the outgoing and returning currents are close together and coaxial, which is a genuinely low-inductance arrangement for a loudspeaker cable.',
    provenanceNote:
      'Construction and metallurgy documented by the manufacturer; gauge inferred from combined cross-section. No published R, L or C, so those are derived.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Absolute Dream',
    cableType: 'speaker',
    price: usd(32000, 'APPROXIMATE', 2024, 'Approximate retail for a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'coaxial',
    gauge: 13,
    dielectric: 'Kapton',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['flagship', 'silver-gold', 'derived-electrical'],
    notes:
      'The flagship loudspeaker cable, using the largest count of parallel mono-crystal silver-gold elements. Even at the top of this range the total copper-equivalent area is about 13 AWG, lighter than an ordinary heavy-gauge copper cable, and the engine computes the divider from that area regardless of the metal it is made of.',
    provenanceNote:
      'Construction and metallurgy documented by the manufacturer; gauge inferred from combined cross-section of the parallel elements. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Dreamline Plus',
    cableType: 'power',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Braided shield',
    termination: ['IEC C15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ecfe8',
    tags: ['mains', 'shielded', 'no-signal-path'],
    notes:
      'A mains cord using the same silver-gold conductor as the signal cables, shielded, with European Schuko and IEC terminations and a 16 A rating. As with every power cord in this database the engine applies no processing whatsoever; the entry records what the cord is made of and what it costs.',
    provenanceNote:
      'Metallurgy, shielding and connector options documented by the manufacturer; gauge and current rating inferred from stated conductor cross-section and the European mains standard.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Van Gogh',
    cableType: 'power',
    price: usd(19000, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord; Art Series.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Multi-layer shield',
    termination: ['IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#9ecfe8',
    tags: ['mains', 'art-series', 'shielded', 'no-signal-path'],
    notes:
      'Art Series mains cord with a heavier silver-gold conductor and a multi-layer shield, offered with C15, C19 and Schuko terminations at 16 A. Construction and current rating are the whole of what is recorded here, because the engine applies no signal processing to mains cables.',
    provenanceNote:
      'Series placement and metallurgy from the manufacturer description; gauge, shield detail and current rating are inferred from the class and the European mains standard rather than quoted, so this entry is marked estimated.',
  }),
  makeCable({
    mfr: 'Crystal Cable',
    model: 'Standard Diamond',
    cableType: 'usb',
    price: usd(650, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m A-to-B assembly.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-gold-alloy',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#9ecfe8',
    tags: ['usb-2.0', 'silver-gold', 'no-signal-path'],
    notes:
      'A USB 2.0 assembly using the house silver-gold conductor for the data pair, foil and braid shielded, terminated A to B. USB audio is packetised and error-checked and an asynchronous DAC generates its own clock, so a compliant cable either carries the data or fails to. The engine applies no signal processing here.',
    provenanceNote:
      'Metallurgy and termination from the manufacturer description; gauge and the 90 ohm figure are the USB 2.0 requirement rather than a published measurement for this assembly, so the entry is marked estimated.',
  }),

  // =========================================================================
  // van den Hul (Netherlands)
  // =========================================================================
  makeCable({
    mfr: 'van den Hul',
    model: 'The Name',
    cableType: 'interconnect',
    price: usd(170, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Linear structured carbon screen over the copper core',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['entry-level', 'hybrid-carbon', 'derived-electrical'],
    notes:
      'Entry hybrid: a copper signal core screened with the house linear structured carbon layer. The carbon screen is a genuinely different material choice for a shield -- its bulk resistivity is orders of magnitude above copper, so it is a poor conductor and an effective damping screen, which is exactly the job a screen has.',
    provenanceNote:
      'Hybrid carbon-over-copper construction is documented by the manufacturer. Gauge is inferred from the stated core diameter. Note that van den Hul does print capacitance and resistance figures on its own data sheets; they are not reproduced from memory here, so the kit derives all three values instead.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'D-102 Mk III',
    cableType: 'interconnect',
    price: usd(420, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 23,
    shielding: 'Dense carbon-loaded screen',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['mid-line', 'hybrid-carbon', 'derived-electrical'],
    notes:
      'A long-running hybrid interconnect: matched copper conductors under a carbon-loaded screen, available single-ended or balanced. Coaxial construction puts the screen close to the core, which keeps capacitance moderate but firmly above what a widely spaced twisted pair would show.',
    provenanceNote:
      'Construction documented by the manufacturer across three generations of this model; gauge inferred from stated conductor diameter. Published capacitance and resistance figures exist on the maker data sheet but are not quoted here, so the kit derives them.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'The First',
    cableType: 'interconnect',
    price: usd(560, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'carbon',
    geometry: 'coaxial',
    gauge: 22,
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['carbon-conductor', 'metal-free-signal-path', 'derived-electrical'],
    notes:
      'The metal-free interconnect: the signal conductor itself is linear structured carbon rather than a metal. That has a large and entirely predictable electrical consequence -- carbon resistivity is roughly three orders of magnitude above copper, so at 22 AWG the derived loop resistance is around 215 ohm per metre rather than the few milliohms a copper pair gives. Over a 1 m run that forms a divider with the input it drives: about 0.04 dB into a 47 kilohm input and about 0.18 dB into 10 kilohms, so the load matters here in a way it does not for any metal cable in this database. In a loudspeaker run the same conductor would be catastrophic, which is why no such cable exists.',
    provenanceNote:
      'Carbon conductor construction is documented by the manufacturer and is the defining feature of this model. Gauge is inferred from the stated conductor diameter, and the resulting derived resistance is high by design rather than by error.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'The Second',
    cableType: 'interconnect',
    price: usd(390, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'carbon',
    geometry: 'coaxial',
    gauge: 23,
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#a3703c',
    tags: ['carbon-conductor', 'metal-free-signal-path', 'derived-electrical'],
    notes:
      'A lighter member of the metal-free carbon interconnect family, with a smaller conductor than The First and therefore a higher series resistance per metre. Same caveat applies: this is only workable because a line input presents a very high impedance.',
    provenanceNote:
      'Placement in the linear structured carbon interconnect family is from the manufacturer range description; the exact conductor dimensions are not to hand, so gauge is inferred and the entry is marked estimated.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'MC Silver IT Mk III',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 23,
    shielding: 'Carbon screen plus silver-plated braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['silver-plated', 'hybrid-carbon', 'derived-electrical'],
    notes:
      'Silver-plated matched-crystal copper conductors under a carbon screen and a plated braid. Because the plating is a surface layer, the kit derives resistance from the copper core; at audio frequencies the skin depth in copper is larger than the plating thickness, so the core is what carries the current.',
    provenanceNote:
      'Conductor plating and dual screen documented by the manufacturer; gauge inferred from stated conductor diameter. Electrical figures derived from the copper core rather than quoted.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: '3T The Rock',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate retail for a 1 m terminated pair.'),
    priceLengthM: 1,
    country: 'Netherlands',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    dielectric: 'Air-foamed polyethylene',
    shielding: 'Carbon screen plus copper braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['3t-series', 'occ', 'derived-electrical'],
    notes:
      'From the 3T range, which uses a continuous-cast copper drawn so the crystal structure runs the length of the cable. The audible claim usually attached to that process is not modelled here; what is modelled is the conductor area and the foamed dielectric, the latter lowering capacitance per metre relative to a solid polyethylene coax.',
    provenanceNote:
      'Conductor process, dielectric and screen documented by the manufacturer; gauge inferred from stated conductor diameter. Data-sheet capacitance figures exist but are not reproduced here, so the kit derives all three values.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'The Clearwater',
    cableType: 'speaker',
    price: usd(210, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair made up from bulk cable.'),
    priceLengthM: 3,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['entry-level', 'bulk-cable', 'derived-electrical'],
    notes:
      'The entry loudspeaker cable, a flat two-conductor stranded copper design sold by the metre. Close side-by-side conductors give low loop inductance and comparatively high capacitance, which for a loudspeaker run is the useful side of the trade -- capacitance across a loudspeaker terminal does essentially nothing, inductance in series with it does not.',
    provenanceNote:
      'Flat two-conductor construction documented by the manufacturer; gauge inferred from the stated cross-section per conductor. Price is for a made-up pair from bulk stock rather than a factory-terminated product.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'CS-122 Hybrid',
    cableType: 'speaker',
    price: usd(430, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['hybrid-carbon', 'derived-electrical'],
    notes:
      'A hybrid loudspeaker cable pairing stranded copper with a carbon layer. The copper is what conducts: carbon resistivity is far too high to carry loudspeaker current, so the kit derives loop resistance from the copper cross-section alone, which is the honest reading of a hybrid construction.',
    provenanceNote:
      'Hybrid construction documented by the manufacturer; gauge inferred from stated copper cross-section. Resistance is derived from the copper conductor only, since the carbon layer carries a negligible share of the current.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'Magnum Hybrid',
    cableType: 'speaker',
    price: usd(950, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['hybrid-carbon', 'derived-electrical'],
    notes:
      'A heavier hybrid with roughly a 12 AWG copper cross-section per polarity plus the carbon layer. The gauge is the meaningful number: over a 3 m run it keeps loop resistance to a few hundredths of an ohm, which against a 4 ohm minimum is a loss of a small fraction of a decibel that the engine computes and reports.',
    provenanceNote:
      'Hybrid construction documented by the manufacturer; gauge inferred from stated copper cross-section. Electrical figures derived rather than taken from the maker data sheet.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: '3T The Cloud',
    cableType: 'speaker',
    price: usd(2700, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Netherlands',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Air-foamed polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['3t-series', 'occ', 'derived-electrical'],
    notes:
      'A 3T loudspeaker cable with a large continuous-cast copper cross-section in a foamed dielectric. Around 11 AWG equivalent, so on a normal domestic run the series resistance is low enough that the loudspeaker impedance curve, not the cable, dominates what the engine computes.',
    provenanceNote:
      'Conductor process and dielectric documented by the manufacturer; gauge inferred from stated cross-section. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'The Mainsstream Hybrid',
    cableType: 'power',
    price: usd(430, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Carbon screen',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['mains', 'hybrid-carbon', 'no-signal-path'],
    notes:
      'A 16 A mains cord with stranded copper conductors and the house carbon screen, terminated Schuko to IEC. Gauge and current rating are real and matter to a power amplifier drawing large transient current. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Construction and screen documented by the manufacturer; gauge and current rating inferred from the stated conductor cross-section and the European mains standard.',
  }),
  makeCable({
    mfr: 'van den Hul',
    model: 'The USB Ultimate',
    cableType: 'usb',
    price: usd(320, 'APPROXIMATE', 2024, 'Approximate retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Netherlands',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid, with separated power and data conductors',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a3703c',
    tags: ['usb-2.0', 'silver-plated', 'no-signal-path'],
    notes:
      'A USB 2.0 assembly with a silver-plated copper data pair kept separate from the bus power conductors inside a foil and braid shield. That separation is sound engineering for a cable carrying both a 480 Mbit/s differential pair and a 5 V supply. The engine applies no signal processing to USB.',
    provenanceNote:
      'Conductor plating, shield and the separation of power from data are documented by the manufacturer; gauge and the 90 ohm figure follow the USB 2.0 requirement rather than a published measurement.',
  }),

  // =========================================================================
  // The Chord Company (United Kingdom)
  // =========================================================================
  makeCable({
    mfr: 'The Chord Company',
    model: 'C-line',
    cableType: 'interconnect',
    price: usd(80, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'Polyethylene',
    shielding: 'Foil shield',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['entry-level', 'single-ended', 'derived-electrical'],
    notes:
      'The entry analogue interconnect: a shielded twisted pair of stranded copper with moulded RCA plugs. Twisted-pair spacing gives moderate capacitance per metre, and at a metre into a normal line input the computed in-band effect is under a hundredth of a decibel.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from the class. Chord does not publish resistance, inductance or capacitance, so the kit derives all three.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Clearway X',
    cableType: 'interconnect',
    price: usd(220, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    dielectric: 'PTFE',
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['mid-line', 'silver-plated', 'derived-electrical'],
    notes:
      'Silver-plated copper conductors in PTFE with a dual shield. PTFE has a lower dielectric constant than polyethylene, which lowers capacitance per metre for the same conductor spacing; that is the one construction difference in this cable with a direct electrical consequence.',
    provenanceNote:
      'Conductor plating, PTFE dielectric and dual shield from the manufacturer description; gauge inferred from the class. Electrical figures derived rather than published.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Shawline X',
    cableType: 'interconnect',
    price: usd(440, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 23,
    dielectric: 'PTFE',
    shielding: 'Foil plus braid with a separate effective shield connection',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['mid-line', 'silver-plated', 'aray-conductor', 'derived-electrical'],
    notes:
      'Adds the house conductor layout, which controls how the conductors are arranged and dressed inside the jacket, to the plated-copper and PTFE recipe. Conductor arrangement is exactly what sets inductance and capacitance, so the geometry is recorded and the kit derives both from it.',
    provenanceNote:
      'Conductor arrangement, dielectric and shield from the manufacturer description; gauge inferred from the class. No published R, L or C figures, so those are derived.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Epic X',
    cableType: 'interconnect',
    price: usd(900, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'PTFE',
    shielding: 'Dual foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['upper-line', 'silver-plated', 'aray-conductor', 'derived-electrical'],
    notes:
      'Heavier plated conductors and a more elaborate shield than the Shawline, in the same PTFE dielectric. Larger conductors lower series resistance; on a line-level interconnect that term is already far below the load impedance, so the engine will report the change as effectively nil and that is the correct answer.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from the class. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Sarum T',
    cableType: 'interconnect',
    price: usd(4900, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    dielectric: 'Taylon',
    shielding: 'Multi-layer foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['flagship-line', 'taylon-dielectric', 'derived-electrical'],
    notes:
      'Uses Taylon as the insulator, a fluoropolymer chosen because it does not go through the phase transition PTFE shows near room temperature. Its dielectric constant is in the same region as PTFE, so the capacitance per metre lands in the same place; the kit treats it accordingly rather than assigning it a bonus.',
    provenanceNote:
      'Taylon dielectric is documented by the manufacturer and is the defining feature of this range; gauge inferred from the class. Electrical figures derived, not published.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'C-screen',
    cableType: 'speaker',
    price: usd(130, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair from bulk cable.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 15,
    shielding: 'Foil screen',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['entry-level', 'screened', 'derived-electrical'],
    notes:
      'Entry screened loudspeaker cable sold by the metre. At 15 AWG the loop resistance over a long run starts to be worth checking: 8 m into a loudspeaker with a 3 ohm minimum puts a tenth of an ohm or so in series, and the engine will show the resulting response tilt rather than hide it.',
    provenanceNote:
      'Screened twin construction from the manufacturer description; gauge inferred from the stated cross-section per conductor. Price is for a made-up pair from bulk stock.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Rumour X',
    cableType: 'speaker',
    price: usd(430, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['mid-line', 'silver-plated', 'derived-electrical'],
    notes:
      'Silver-plated copper in PTFE, twisted, terminated with the maker hardware. The plating is a surface layer so loop resistance derives from the copper core and the 14 AWG cross-section, which over a typical 3 m run costs a few hundredths of a decibel into an 8 ohm load.',
    provenanceNote:
      'Conductor plating and dielectric from the manufacturer description; gauge inferred from stated cross-section. Electrical figures derived from copper resistivity and the twisted geometry.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'EpicXL',
    cableType: 'speaker',
    price: usd(1900, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['upper-line', 'silver-plated', 'derived-electrical'],
    notes:
      'A substantially heavier conductor set than the Rumour, around 12 AWG per polarity, in PTFE with the house conductor arrangement. Doubling conductor area halves loop resistance, which is the single largest electrical difference between any two loudspeaker cables of the same length.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from stated cross-section. No published R, L or C, so the kit derives them.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'ChordMusic',
    cableType: 'speaker',
    price: usd(13000, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Taylon',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['flagship', 'taylon-dielectric', 'derived-electrical'],
    notes:
      'The flagship loudspeaker cable, Taylon insulated with the largest conductor cross-section in the range. Of the three numbers the engine reads, the gauge here is genuinely good and the dielectric choice affects capacitance, which a loudspeaker load is almost entirely insensitive to.',
    provenanceNote:
      'Taylon dielectric and construction documented by the manufacturer; gauge inferred from stated cross-section. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'C-power',
    cableType: 'power',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 13,
    shielding: 'Foil shield',
    termination: ['IEC C13', 'UK 13 A'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['mains', 'shielded', 'no-signal-path'],
    notes:
      'Entry mains cord: stranded copper, foil screened, fitted with a fused UK 13 A plug and a C13 connector. The fuse rating and the conductor gauge are what determine what it may safely feed. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Construction and connector fitment from the manufacturer description; gauge and current rating follow the UK 13 A mains standard the cord is built to.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Shawline Power',
    cableType: 'power',
    price: usd(650, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C15', 'UK 13 A', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['mains', 'shielded', 'no-signal-path'],
    notes:
      'A heavier screened mains cord with silver-plated conductors and a choice of IEC and mains-plug terminations, rated to the 13 A UK standard. Build quality and connector options are the entirety of what is recorded, because the engine applies no processing to mains cables.',
    provenanceNote:
      'Construction and connector options from the manufacturer description; gauge and current rating follow the mains standard rather than a published AWG figure.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'C-USB',
    cableType: 'usb',
    price: usd(70, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['usb-2.0', 'compliant', 'no-signal-path'],
    notes:
      'A compliant USB 2.0 A-to-B assembly with a shielded 90 ohm data pair. USB audio is packetised and error-checked, and in an asynchronous DAC the clock is generated at the DAC rather than recovered from the cable. The engine applies no signal processing here.',
    provenanceNote:
      'Construction from the manufacturer description; the 90 ohm figure and gauge follow the USB 2.0 specification rather than a published measurement for this assembly.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'C-stream',
    cableType: 'ethernet',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 100,
    digitalStandard: 'Cat 6, 100 ohm',
    shielding: 'Unshielded twisted pairs',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['cat6', 'no-signal-path'],
    notes:
      'A Cat 6 patch lead built to the 100 ohm standard with moulded RJ45 connectors. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of audio ahead of playback, so there is no analog path between the two ends. The engine applies no signal processing here.',
    provenanceNote:
      'Category and construction from the manufacturer description; gauge and the 100 ohm figure are the Cat 6 requirement rather than a published measurement for this lead.',
  }),
  makeCable({
    mfr: 'The Chord Company',
    model: 'Shawline Streaming',
    cableType: 'ethernet',
    price: usd(430, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7, 100 ohm',
    shielding: 'Individually foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a4fcf',
    tags: ['cat7', 'shielded', 'no-signal-path'],
    notes:
      'A fully shielded Cat 7 patch lead with silver-plated conductors and individually foiled pairs. That construction is what a shielded category cable is supposed to be, and it is also what the engine does nothing with: ethernet carries error-checked packets, not an analog waveform.',
    provenanceNote:
      'Category, shielding and conductor plating from the manufacturer description; gauge and the 100 ohm figure follow the category standard rather than a published measurement.',
  }),

  // =========================================================================
  // QED (United Kingdom)
  // =========================================================================
  makeCable({
    mfr: 'QED',
    model: 'Profile 42 Strand',
    cableType: 'speaker',
    price: usd(45, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m pair cut from a bulk reel.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 16,
    termination: ['bare', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['entry-level', 'bulk-cable', 'derived-electrical'],
    notes:
      'A plain 42-strand figure-of-eight copper cable, about 1.3 square millimetres per conductor. This is the baseline: at 16 AWG over 5 m into a loudspeaker dipping to 3 ohms, loop resistance is around a fifth of an ohm and the engine will show a visible response deviation, which is the case for using heavier cable on long runs and the only such case that survives measurement.',
    provenanceNote:
      'Strand count and cross-section are published by the manufacturer; gauge here is converted from the stated square-millimetre figure. QED does print loop resistance, inductance and capacitance on its data sheets, but those figures are not reproduced from memory here, so the kit derives all three.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'XT25',
    cableType: 'speaker',
    price: usd(130, 'CURRENT_RETAIL', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 13,
    dielectric: 'Polyethylene, hollow tube core',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['x-tube', 'low-inductance', 'derived-electrical'],
    notes:
      'Uses the house hollow-tube construction, where the conductor strands are arranged around a non-conducting core so the current is distributed on a larger circumference. That arrangement reduces loop inductance relative to a solid bundle of the same area; total copper is about 2.5 square millimetres, near 13 AWG.',
    provenanceNote:
      'Tube construction and the 2.5 square millimetre cross-section are published by the manufacturer; the AWG value here is converted from that figure. Published R, L and C exist on the maker data sheet but are not quoted from memory, so the kit derives them.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'XT40i',
    cableType: 'speaker',
    price: usd(230, 'CURRENT_RETAIL', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 11,
    dielectric: 'Polyethylene, hollow tube core',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['x-tube', 'low-inductance', 'derived-electrical'],
    notes:
      'The 4 square millimetre version of the hollow-tube design, roughly 11 AWG. Heavier copper cuts loop resistance in half relative to the 2.5 square millimetre model, which is the difference that shows up in the amplifier-to-loudspeaker divider the engine computes; the tube geometry keeps inductance low at the same time.',
    provenanceNote:
      'Tube construction and the 4 square millimetre cross-section are published by the manufacturer; AWG is converted from that figure. Data-sheet R, L and C are not reproduced here, so those are derived.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Silver Anniversary XT',
    cableType: 'speaker',
    price: usd(370, 'CURRENT_RETAIL', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'concentric',
    gauge: 13,
    dielectric: 'Polyethylene, hollow tube core',
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['x-tube', 'silver-plated', 'derived-electrical'],
    notes:
      'Silver-plated conductors on the hollow-tube former. Because plating is a surface layer far thinner than the skin depth in copper at audio frequencies, the kit derives resistance from the copper core; the geometry, not the plating, is what makes this cable measure differently from a plain twin.',
    provenanceNote:
      'Plating and tube construction are published by the manufacturer; gauge converted from the stated cross-section. Loop resistance, inductance and capacitance appear on the maker data sheet but are derived here rather than quoted from memory.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Genesis Silver Spiral',
    cableType: 'speaker',
    price: usd(1300, 'CURRENT_RETAIL', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['upper-line', 'silver-plated', 'low-inductance', 'derived-electrical'],
    notes:
      'Counter-spiralled silver-plated conductor groups. Winding the two polarities in opposition means adjacent conductors carry opposing current, cancelling much of the external field and lowering loop inductance, with higher capacitance as the unavoidable other half of the trade.',
    provenanceNote:
      'Spiral construction and plating are published by the manufacturer; gauge converted from the stated cross-section. Electrical figures derived rather than quoted.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Supremus',
    cableType: 'speaker',
    price: usd(2000, 'CURRENT_RETAIL', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'concentric',
    gauge: 11,
    dielectric: 'Polyethylene, hollow tube core',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['flagship', 'x-tube', 'low-inductance', 'derived-electrical'],
    notes:
      'The flagship, combining the largest hollow-tube conductor in the range with silver plating and heavier termination hardware. Around 11 AWG of copper on a tube former: low loop resistance and low inductance at once, which is the combination that actually minimises what a cable does to the amplifier-to-loudspeaker transfer.',
    provenanceNote:
      'Construction is published by the manufacturer; gauge converted from the stated cross-section. Data-sheet electrical figures are not reproduced from memory, so the kit derives all three.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Performance Audio 40i',
    cableType: 'interconnect',
    price: usd(75, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Foil plus braid',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['entry-level', 'single-ended', 'derived-electrical'],
    notes:
      'A shielded coaxial analogue interconnect with moulded RCA terminations. Coax puts the return close around the signal core, which is good for noise rejection and gives moderate capacitance per metre; over a metre into a line input the engine computes an in-band effect of essentially zero.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from the class. Electrical figures derived by the kit rather than quoted from the data sheet.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Reference Audio 40',
    cableType: 'interconnect',
    price: usd(190, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 23,
    dielectric: 'Foamed polyethylene',
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['mid-line', 'silver-plated', 'derived-electrical'],
    notes:
      'Silver-plated conductors in a foamed dielectric under a dual shield, offered single-ended or balanced. The foamed dielectric is the electrically relevant part: lower permittivity means lower capacitance per metre for the same spacing.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from the class. Published figures exist on the maker data sheet but are derived here instead.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Signature Audio 40',
    cableType: 'interconnect',
    price: usd(320, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 22,
    dielectric: 'Foamed polyethylene',
    shielding: 'Multi-layer foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['upper-line', 'silver-plated', 'derived-electrical'],
    notes:
      'The top analogue interconnect in the range: larger plated conductors, a heavier shield stack and better connector hardware. None of those changes moves the computed in-band response of a 1 m line-level link away from flat, and the simulation will say so.',
    provenanceNote:
      'Construction from the manufacturer description; gauge inferred from the class. Electrical figures derived rather than quoted.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Performance USB',
    cableType: 'usb',
    price: usd(55, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['usb-2.0', 'compliant', 'no-signal-path'],
    notes:
      'A compliant shielded USB 2.0 assembly with a 90 ohm data pair. The relevant engineering is impedance control and shielding, both of which determine whether the link runs error-free at 480 Mbit/s. The engine applies no signal processing to USB.',
    provenanceNote:
      'Construction from the manufacturer description; gauge and impedance follow the USB 2.0 specification rather than a published measurement for this assembly.',
  }),
  makeCable({
    mfr: 'QED',
    model: 'Reference USB A-B',
    cableType: 'usb',
    price: usd(120, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid with separated power conductors',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e03c31',
    tags: ['usb-2.0', 'silver-plated', 'no-signal-path'],
    notes:
      'Silver-plated data conductors held to 90 ohms with the bus power pair routed separately inside the shield. Sound construction for a high-speed serial link, and irrelevant to the engine, which applies no processing to USB because the data either arrives intact or the transfer fails.',
    provenanceNote:
      'Construction from the manufacturer description; gauge and impedance follow the USB 2.0 specification rather than published measurements.',
  }),

  // =========================================================================
  // Atlas Cables (United Kingdom)
  // =========================================================================
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Element Integra',
    cableType: 'interconnect',
    price: usd(90, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Braided copper shield',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['entry-level', 'single-ended', 'derived-electrical'],
    notes:
      'The entry interconnect: stranded copper in a shielded coaxial format with the house cold-welded plug, which forms the conductor-to-plug joint by pressure rather than solder. A cold-welded joint is a mechanical and reliability decision; electrically it removes one solder interface from a path whose total resistance is already milliohms.',
    provenanceNote:
      'Construction and the cold-weld termination are documented by the manufacturer; gauge inferred from the class. Atlas does not publish resistance, inductance or capacitance, so the kit derives all three.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Hyper dd',
    cableType: 'interconnect',
    price: usd(280, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 23,
    dielectric: 'Dual dielectric, foamed and solid layers',
    shielding: 'Foil plus braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['mid-line', 'occ', 'derived-electrical'],
    notes:
      'Continuous-cast copper conductors in a two-layer dielectric, shielded, available balanced. The layered dielectric raises the effective conductor spacing and lowers the average permittivity, both of which reduce capacitance per metre relative to a solid-insulated pair of the same diameter.',
    provenanceNote:
      'Conductor process and dual dielectric documented by the manufacturer; gauge inferred from the class. Electrical figures derived rather than published.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Mavros Ultra',
    cableType: 'interconnect',
    price: usd(1200, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    dielectric: 'Foamed polyethylene',
    shielding: 'Multi-layer foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['upper-line', 'occ', 'derived-electrical'],
    notes:
      'Upper continuous-cast copper interconnect with a heavier shield stack and larger conductors. The shielding matters for rejecting interference in a real room; it has no bearing on the three per-metre figures the DSP reads, which come from conductor area and spacing.',
    provenanceNote:
      'Construction documented by the manufacturer; gauge inferred from the class. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Asimi Ultra',
    cableType: 'interconnect',
    price: usd(3200, 'CURRENT_RETAIL', 2024, 'Retail for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Foamed polyethylene',
    shielding: 'Multi-layer foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['flagship', 'silver', 'occ', 'derived-electrical'],
    notes:
      'The flagship interconnect, using continuous-cast silver rather than copper. Silver resistivity is about eight per cent below copper, so at the same gauge the series resistance is about eight per cent lower -- a real and calculable difference, and one that amounts to a few milliohms on a line-level cable.',
    provenanceNote:
      'Silver conductor process documented by the manufacturer; gauge inferred from the class. Electrical figures derived from silver resistivity and the twisted geometry, not published.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Element',
    cableType: 'speaker',
    price: usd(160, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 15,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['entry-level', 'derived-electrical'],
    notes:
      'Entry loudspeaker cable, stranded copper twin with cold-welded plugs. At 15 AWG this is a fine choice for a short run and a marginal one for a long run into a low-impedance loudspeaker, which is precisely the distinction the engine makes visible when the length is changed.',
    provenanceNote:
      'Construction and termination documented by the manufacturer; gauge inferred from the stated cross-section per conductor. Electrical figures derived by the kit.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Equator',
    cableType: 'speaker',
    price: usd(270, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['entry-level', 'derived-electrical'],
    notes:
      'A step up in copper cross-section from the entry cable in the same twisted twin format. The only electrical change is loop resistance, which falls in proportion to conductor area, and that is the term that adds to the amplifier output impedance seen by the loudspeaker.',
    provenanceNote:
      'Construction documented by the manufacturer; gauge inferred from stated cross-section per conductor. No published electrical figures, so those are derived.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Hyper',
    cableType: 'speaker',
    price: usd(550, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['mid-line', 'occ', 'derived-electrical'],
    notes:
      'Continuous-cast copper twin at roughly 13 AWG. Continuous casting is a real metallurgical process producing long crystal boundaries; its resistivity is within a fraction of a per cent of ordinary oxygen-free copper, so the kit treats it as such and lets the gauge do the work.',
    provenanceNote:
      'Conductor process documented by the manufacturer; gauge inferred from stated cross-section. Resistivity is treated as effectively that of oxygen-free copper, which is the measured position rather than the marketing one.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Ascent',
    cableType: 'speaker',
    price: usd(950, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['mid-line', 'occ', 'bi-wire-available', 'derived-electrical'],
    notes:
      'Heavier continuous-cast copper, offered single-wired or bi-wired. Around 12 AWG per polarity keeps loop resistance over a 3 m run to a few hundredths of an ohm, at which point the loudspeaker impedance curve dominates the result the engine computes.',
    provenanceNote:
      'Construction and wiring options documented by the manufacturer; gauge inferred from stated cross-section. Electrical figures derived rather than published.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Mavros',
    cableType: 'speaker',
    price: usd(1900, 'CURRENT_RETAIL', 2024, 'Retail for a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Foamed polyethylene',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['flagship', 'occ', 'heavy-gauge', 'derived-electrical'],
    notes:
      'The top continuous-cast copper loudspeaker cable, about 11 AWG per polarity in a foamed dielectric. Of everything in this brand range, the gauge is the number that changes what the simulation reports, and it is the number that costs the least to specify.',
    provenanceNote:
      'Construction documented by the manufacturer; gauge inferred from stated cross-section. No published R, L or C, so the kit derives them.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Eos 4dd',
    cableType: 'power',
    price: usd(750, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 13,
    shielding: 'Foil plus braid',
    termination: ['IEC C13', 'IEC C19', 'UK 13 A', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['mains', 'high-current', 'shielded', 'no-signal-path'],
    notes:
      'A heavy shielded mains cord using four continuous-cast copper conductors in a dual-dielectric jacket, offered with C13 and C19 outlets. The large conductor cross-section is genuinely useful for an amplifier drawing high transient current from the wall. The engine applies no signal processing to power cords, so this entry changes the chain description and the price only.',
    provenanceNote:
      'Conductor count, dielectric and connector options documented by the manufacturer; gauge and current rating inferred from stated cross-section and the UK 13 A mains standard.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Element USB',
    cableType: 'usb',
    price: usd(110, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['usb-2.0', 'compliant', 'no-signal-path'],
    notes:
      'A shielded USB 2.0 A-to-B assembly built to the 90 ohm data-pair requirement. USB audio is packetised and error-checked and an asynchronous DAC clocks itself, so the cable either carries the data or it does not. The engine applies no signal processing here.',
    provenanceNote:
      'Construction from the manufacturer description; gauge and impedance follow the USB 2.0 specification rather than a published measurement for this assembly.',
  }),
  makeCable({
    mfr: 'Atlas Cables',
    model: 'Mavros USB',
    cableType: 'usb',
    price: usd(650, 'CURRENT_RETAIL', 2024, 'Retail for a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid, data pair separated from bus power',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f5f',
    tags: ['usb-2.0', 'occ', 'no-signal-path'],
    notes:
      'Continuous-cast copper conductors with the 90 ohm data pair routed apart from the 5 V bus supply inside a foil and braid shield. That is careful construction for a high-speed serial link; it is not a signal-path change, and the engine applies none.',
    provenanceNote:
      'Conductor process and shield arrangement from the manufacturer description; gauge and impedance follow the USB 2.0 specification rather than published measurements.',
  }),
];
